import { TpBitmapCoordinates } from '../../data/interfaces';
import { TpWebGL2DRenderDataBitmap, TpWebGLRenderDataLayer } from './interface-renderData';
import { TpWebGL2DRenderLayer, TpWebGl2DContext, WebGL2DRenderTypes } from './interfaces';
import { setWebGLZoomPan } from './utils';

export interface TpRenderBitmap {
  renderType: WebGL2DRenderTypes.BITMAP;
  isActive: boolean;
  opacity: number;
  ctx: TpWebGl2DContext;
  layerId: string;
  rectangle: TpBitmapCoordinates | null;
  imageData: HTMLImageElement | ImageBitmap | null;
  positionBuffer: any;
  texCoordBuffer: any;
  texture: any;
}

function createQuadVertices(rectangle: TpBitmapCoordinates) {
  return new Float32Array([
    rectangle.x,
    rectangle.y,
    rectangle.x + rectangle.width,
    rectangle.y,
    rectangle.x + rectangle.width,
    rectangle.y - rectangle.height,

    rectangle.x,
    rectangle.y,
    rectangle.x + rectangle.width,
    rectangle.y - rectangle.height,
    rectangle.x,
    rectangle.y - rectangle.height,
  ]);
}

function create(ctx: TpWebGl2DContext, dataInp: TpWebGLRenderDataLayer): TpRenderBitmap {
  const gl = ctx.gl;
  const data = dataInp as TpWebGL2DRenderDataBitmap;

  // Create a buffer for the vertices, forming a quad at the position of the bitmap
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    createQuadVertices(data.rectangle ?? { x: 0, y: 0, width: 1, height: 1 }),
    gl.STATIC_DRAW,
  );

  // Create a buffer for the corresponding texture coordinates of the vertex quad.
  const texCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
  const texCoords = [0.0, 0.0, 1.0, 0.0, 1.0, 1.0, 0.0, 0.0, 1.0, 1.0, 0.0, 1.0];
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(texCoords), gl.STATIC_DRAW);

  // Create the texture object
  const texture = gl.createTexture();
  if (data.imageData) {
    // load the bitmap into the texture, if applicable
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data.imageData); // Upload the image into the texture.
  }

  const renderCtx: TpRenderBitmap = {
    isActive: data.isActive,
    opacity: data.opacity,
    renderType: WebGL2DRenderTypes.BITMAP,
    layerId: data.layerId,
    ctx,
    rectangle: data.rectangle,
    imageData: data.imageData,
    positionBuffer,
    texCoordBuffer,
    texture,
  };

  return renderCtx;
}

function updateData(rendererInp: TpWebGL2DRenderLayer, dataInp: TpWebGLRenderDataLayer) {
  const renderer = rendererInp as TpRenderBitmap;
  const gl = renderer.ctx.gl;
  const data = dataInp as TpWebGL2DRenderDataBitmap;

  renderer.isActive = data.isActive;
  renderer.opacity = data.opacity;

  // recreate the quad
  gl.bindBuffer(gl.ARRAY_BUFFER, renderer.positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    createQuadVertices(data.rectangle ?? { x: 0, y: 0, width: 1, height: 1 }),
    gl.STATIC_DRAW,
  );

  if (data.imageData && data.imageData !== renderer.imageData) {
    // update the texture bitmap
    gl.bindTexture(gl.TEXTURE_2D, renderer.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data.imageData);
    renderer.imageData = data.imageData;
  }
}

function performRender(webGlCtx: TpWebGl2DContext, renderCtxInp: TpWebGL2DRenderLayer) {
  const renderCtx = renderCtxInp as TpRenderBitmap;
  if (!renderCtx.isActive) return;
  const gl = webGlCtx.gl;

  const myProgram = webGlCtx.programContextBitmap;
  gl.useProgram(myProgram.program);

  setWebGLZoomPan(webGlCtx, myProgram);

  gl.uniform1f(myProgram.locFixedOpacity!, renderCtx.opacity);

  // gl.enable(gl.BLEND);
  // gl.blendEquation(gl.FUNC_ADD);
  // gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  // Vertices
  gl.enableVertexAttribArray(myProgram.locAttribPosition);
  gl.bindBuffer(gl.ARRAY_BUFFER, renderCtx.positionBuffer);
  gl.vertexAttribPointer(myProgram.locAttribPosition, 2, gl.FLOAT, false, 0, 0);

  // Texture coords
  gl.enableVertexAttribArray(myProgram.locAttribTexCoord!);
  gl.bindBuffer(gl.ARRAY_BUFFER, renderCtx.texCoordBuffer);
  gl.vertexAttribPointer(myProgram.locAttribTexCoord!, 2, gl.FLOAT, false, 0, 0);

  // Texture
  gl.bindTexture(gl.TEXTURE_2D, renderCtx.texture);

  // Set the parameters so we can render any size image.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); //gl.NEAREST
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  gl.drawArrays(gl.TRIANGLES, 0, 6); // Draw the actual quad
}

export const webGl2DBitmap = {
  create,
  updateData,
  performRender,
};
